import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuthStatus } from "@/hooks/useAuth";
import { NoindexSeo } from "@/components/Seo";

/**
 * Route accessible aux non-connectés (pages login, register).
 *
 * Implémentation : on rend TOUJOURS les enfants tant qu'on n'a pas la
 * preuve que l'utilisateur est connecté. Si l'auth se résout en
 * `isAuthenticated=true` plus tard, <Navigate /> redirige immédiatement.
 *
 * Pourquoi pas de gate `if (isLoading) return null` : avec TanStack Query v5
 * + StrictMode + le refetch-on-mount automatique, la query /me peut
 * re-passer en `status: 'pending'` après avoir reçu un 401 (re-fetch en
 * arrière-plan qui réinitialise le statut localement). La gate se déclenche
 * alors et la page reste blanche. Pour les pages publiques, c'est sans risque
 * de rendre les enfants par défaut : la pire conséquence est qu'un user
 * déjà connecté voit furtivement le formulaire avant la redirection.
 *
 * noindex : les pages d'auth n'ont pas vocation à être indexées.
 */
export function RequireGuest({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthStatus();
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <>
      <NoindexSeo />
      {children}
    </>
  );
}
